import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { AppHeader } from '../components/AppHeader';

type Step =
  | { kind: 'email' }
  | { kind: 'code'; email: string; resendIn: number };

const RESEND_COOLDOWN_SECONDS = 60;
const DEFAULT_RETURN_TO = '/portal';

/**
 * Normalise the `?returnTo=` query parameter into a same-origin portal path.
 * Falls back to /portal whenever the value looks like an open-redirect attempt
 * (absolute URL, protocol-relative, traversal) or targets a route outside the
 * portal. The server runs the same check on the OIDC side; we duplicate it
 * here so the email-code flow stays trustworthy without any extra round-trip.
 */
function sanitizeReturnTo(raw: string | null): string {
  if (!raw) return DEFAULT_RETURN_TO;
  if (!raw.startsWith('/')) return DEFAULT_RETURN_TO;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return DEFAULT_RETURN_TO;
  if (raw !== '/portal' && !raw.startsWith('/portal/') && !raw.startsWith('/portal?')) {
    return DEFAULT_RETURN_TO;
  }
  return raw;
}

export function LoginPage() {
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  // Computed once: the URL is the only source of returnTo at this stage, and
  // we don't want a re-render between the email step and the code step to
  // change the destination underneath the user.
  const returnTo = useMemo(
    () => sanitizeReturnTo(searchParams.get('returnTo')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div className="flex min-h-screen flex-col bg-wc-gray">
      <AppHeader />
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        {step.kind === 'email' ? (
          <EmailStep
            submitting={submitting}
            setSubmitting={setSubmitting}
            message={message}
            setMessage={setMessage}
            returnTo={returnTo}
            onCodeStep={(email) => setStep({ kind: 'code', email, resendIn: RESEND_COOLDOWN_SECONDS })}
          />
        ) : (
          <CodeStep
            email={step.email}
            initialCooldown={step.resendIn}
            returnTo={returnTo}
            onBack={() => {
              setStep({ kind: 'email' });
              setMessage(null);
            }}
          />
        )}
      </main>
      <footer className="border-t border-wc-muted/20 bg-wc-white">
        <div className="mx-auto flex max-w-portal items-center justify-between px-6 py-4 text-xs text-wc-muted">
          <span>© Weigandt Consulting</span>
          <a
            className="hover:text-wc-blue"
            href="https://www.weigandt-consulting.com"
            target="_blank"
            rel="noreferrer"
          >
            weigandt-consulting.com
          </a>
        </div>
      </footer>
    </div>
  );
}

interface EmailStepProps {
  submitting: boolean;
  setSubmitting: (v: boolean) => void;
  message: string | null;
  setMessage: (m: string | null) => void;
  returnTo: string;
  onCodeStep: (email: string) => void;
}

function EmailStep({ submitting, setSubmitting, message, setMessage, returnTo, onCodeStep }: EmailStepProps) {
  const [email, setEmail] = useState('');

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    setSubmitting(true);
    try {
      const trimmed = email.trim();
      const result = await api.discover(trimmed);
      if (result.flow === 'oidc') {
        // Forward returnTo to the server-side begin-login step so the
        // post-callback redirect can land the user back on the original deep
        // link. The server re-validates it (see AuthService.sanitizeReturnTo)
        // to guard against open-redirect abuse.
        const sep = result.loginUrl.includes('?') ? '&' : '?';
        const target = returnTo === DEFAULT_RETURN_TO
          ? result.loginUrl
          : `${result.loginUrl}${sep}returnTo=${encodeURIComponent(returnTo)}`;
        window.location.assign(target);
        return;
      }
      if (result.flow === 'email_code') {
        try {
          await api.requestEmailCode(trimmed);
        } catch {
          // best-effort: even if request fails the user can still try the
          // resend button on the next step, which surfaces the error.
        }
        onCodeStep(trimmed);
        return;
      }
      setMessage(
        "We couldn't find a portal for that email. Please contact your Weigandt point of contact.",
      );
    } catch {
      setMessage('Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card w-full max-w-md p-10">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-wc-blue">
        Customer portal
      </p>
      <h1 className="mt-4 font-serif text-3xl leading-tight text-wc-black">Welcome back.</h1>
      <p className="mt-3 text-sm text-wc-muted-strong">
        Sign in with your work email. We&apos;ll route you to your organisation&apos;s
        identity provider or send a one-time code, depending on how your portal
        is configured.
      </p>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
        <div>
          <label className="field-label" htmlFor="email">
            Work email
          </label>
          <input
            id="email"
            className="field-input"
            type="email"
            autoComplete="email"
            placeholder="you@your-company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting}
          />
        </div>

        {message && (
          <p className="text-sm text-wc-red" role="alert">
            {message}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={submitting || !email}>
          {submitting ? 'Looking up…' : 'Continue'}
        </button>
      </form>
    </div>
  );
}

interface CodeStepProps {
  email: string;
  initialCooldown: number;
  returnTo: string;
  onBack: () => void;
}

function CodeStep({ email, initialCooldown, returnTo, onBack }: CodeStepProps) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(initialCooldown);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const handleVerify = async (event: FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await api.verifyEmailCode(email, code);
      navigate(returnTo);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('That code is incorrect or has expired. Try again or request a new one.');
      } else if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Request a new code.');
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setError(null);
    try {
      await api.requestEmailCode(email);
      setResendIn(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError('Could not resend the code. Try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="card w-full max-w-md p-10">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-wc-blue">Check your email</p>
      <h1 className="mt-4 font-serif text-3xl leading-tight text-wc-black">Enter your code.</h1>
      <p className="mt-3 text-sm text-wc-muted-strong">
        We sent a 6-digit code to{' '}
        <span className="font-medium text-wc-black">{maskEmail(email)}</span>. It expires in
        10 minutes.
      </p>

      <form className="mt-8 space-y-5" onSubmit={handleVerify} noValidate>
        <div>
          <label className="field-label" htmlFor="code">
            Sign-in code
          </label>
          <input
            id="code"
            className="field-input text-center font-mono text-2xl tracking-[0.4em]"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
            required
            disabled={submitting}
          />
        </div>

        {error && (
          <p className="text-sm text-wc-red" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={submitting || code.length !== 6}>
          {submitting ? 'Verifying…' : 'Sign in'}
        </button>

        <div className="flex items-center justify-between text-xs text-wc-muted-strong">
          <button
            type="button"
            onClick={onBack}
            className="hover:text-wc-blue"
            disabled={submitting}
          >
            Use a different email
          </button>
          <button
            type="button"
            onClick={handleResend}
            className="hover:text-wc-blue disabled:cursor-not-allowed disabled:opacity-60"
            disabled={resendIn > 0 || resending || submitting}
          >
            {resending
              ? 'Resending…'
              : resendIn > 0
                ? `Resend in ${resendIn}s`
                : 'Resend code'}
          </button>
        </div>
      </form>
    </div>
  );
}

function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at < 2) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  const head = local.slice(0, Math.min(2, local.length - 1));
  return `${head}${'•'.repeat(Math.max(1, local.length - head.length))}${domain}`;
}
